// Thumbnails for pages whose bytes the client no longer holds.
//
// Nothing about a thumbnail is persisted — a data URL per page would dwarf the
// rest of the snapshot — so a restored page arrives on the placeholder holding
// only its fileUrl. Images already are their own thumbnail; PDFs have to be
// fetched and re-rendered.
//
// Every failure path returns no entry for the page, leaving the placeholder it
// already had. A draft whose previews will not draw is still one the teacher has
// to be able to organise and submit, and the url is a 302 to signed GCS storage,
// so CORS can fail here in a way it cannot anywhere else in the wizard.
import { loadPdfFromUrl, generatePdfPageThumbnail } from './pdfUtils';
import {
  isPlaceholderThumbnail,
  imageThumbnailUrl,
} from './thumbnails';
import { isManual, pageIndexOf, fileNameOf } from './pageRules';
import { getFileType } from './fileTypes';
import { groupBy } from './groupBy';
import { canPreviewWord, filePreviewUrl, isWordFile } from '../../../utils/filePreview';

const RENDERABLE = new Set(['pdf', 'image', 'unknown']);

// Three files open at a time. Unbounded, a 1000-page resume opens every PDF at
// once and holds every page canvas live until the last one settles.
const MAX_FILES_IN_FLIGHT = 3;

const typeOf = (page) => {
  if (isWordFile(page.fileUrl)) {
    return canPreviewWord(filePreviewUrl(page.fileUrl)) ? 'pdf' : 'word';
  }
  const type = getFileType(fileNameOf(page).split(/[?#]/)[0]);
  // GridFS URLs have no extension; a Word original may also have been
  // normalised to PDF under an opaque storage key. Read its actual media type.
  return type === 'word' ? 'unknown' : type;
};

const needsThumbnail = (page) =>
  !isManual(page) &&
  Boolean(page?.fileUrl) &&
  RENDERABLE.has(typeOf(page)) &&
  isPlaceholderThumbnail(page.thumbnail);

// pdf.js pages are 1-based; sourcePageIndex is the 0-based position within the
// file, which is the whole reason it is persisted.
const pageNumberOf = (page) => pageIndexOf(page) + 1;

/**
 * Sequential per file, matching getPdfInfo's "render sequentially to avoid memory
 * spikes on large PDFs". Each page canvas is ~3 MB while it is live, so rendering
 * a whole file at once is the difference between 3 MB and 3 GB.
 */
const renderPdfPages = async (fileUrl, pages) => {
  const rendered = [];
  let pdf;
  try {
    pdf = await loadPdfFromUrl(fileUrl);
    for (const page of pages) {
      // eslint-disable-next-line no-await-in-loop -- see note above
      const thumbnail = await generatePdfPageThumbnail(pdf, pageNumberOf(page));
      if (!isPlaceholderThumbnail(thumbnail)) rendered.push([page.id, thumbnail]);
    }
  } catch (error) {
    // Thumbnail re-rendering failed
  } finally {
    await pdf?.destroy?.();
  }
  return rendered;
};

const renderFile = async ([fileUrl, pages]) => {
  let type = typeOf(pages[0]);
  if (type === 'unknown') {
    try {
      // Storage redirects are signed for GET. HEAD follows the redirect with
      // the wrong signed method and is rejected by GCS. Read only the headers
      // of a GET, then release the body before PDF.js opens the actual file.
      const response = await fetch(fileUrl, { method: 'GET', credentials: 'omit' });
      try {
        if (!response.ok) return [];
        const mime = response.headers.get('content-type') || '';
        type = mime.startsWith('image/') ? 'image' : mime.includes('application/pdf') ? 'pdf' : 'word';
      } finally {
        try { await response.body?.cancel?.(); } catch { /* Headers are already available. */ }
      }
    } catch {
      return [];
    }
  }
  if (type === 'image') return pages.map((page) => [page.id, imageThumbnailUrl(fileUrl)]);
  return type === 'pdf' ? renderPdfPages(fileUrl, pages) : [];
};

// A shared cursor over one list is enough: each worker takes the next file when
// it finishes one, so at most MAX_FILES_IN_FLIGHT are ever open.
const renderInBatches = async (files) => {
  const queue = [...files];
  const worker = async (collected) => {
    while (queue.length) collected.push(...(await renderFile(queue.shift())));
    return collected;
  };
  const workers = Array.from(
    { length: Math.min(MAX_FILES_IN_FLIGHT, files.length) },
    () => worker([]),
  );
  return (await Promise.all(workers)).flat();
};

/**
 * @param {Array} pages wizard pages, restored or freshly uploaded
 * @returns {Promise<Object>} thumbnail by page id, holding only what rendered —
 *   a page absent from the result keeps the placeholder it already has.
 */
export const resumedThumbnails = async (pages) => {
  const wanted = (pages || []).filter(needsThumbnail);
  if (wanted.length === 0) return {};
  const grouped = groupBy(wanted, (page) => page.fileUrl);
  return Object.fromEntries(await renderInBatches(grouped));
};
